import {React} from '@api'
import {Rect, TabSetNode, BorderNode, Orientation, TabNode} from '@flexlayout'

/** @hidden @internal */
/**
 * TODO - move the state in here to be in the layout store itself instead of needing to call this hook everywhere to get the values over and over
 * @param node
 * @param orientation
 * @param toolbarRef
 * @param stickyButtonsRef
 */
export const useTabOverflow_deprecated = (
  node: TabSetNode | BorderNode,
  orientation: Orientation,
  toolbarRef: React.MutableRefObject<HTMLDivElement | null>,
  stickyButtonsRef: React.MutableRefObject<HTMLDivElement | null>
) => {
  const firstRender = React.useRef<boolean>(true)
  const tabsTruncated = React.useRef<boolean>(false)
  const lastRect = React.useRef<Rect>(new Rect(0, 0, 0, 0))
  const selfRef = React.useRef<HTMLDivElement | null>(null)

  const [position, setPosition] = React.useState<number>(0)
  const userControlledLeft = React.useRef<boolean>(false)
  const [hiddenTabs, setHiddenTabs] = React.useState<{ node: TabNode; index: number }[]>([])

  // console.debug(`useTabOverflow`, {id: node.id})
  // if selected node or tabset/border rectangle change then unset usercontrolled (so selected tab will be kept in view)
  React.useLayoutEffect(() => {
    userControlledLeft.current = false
  }, [node.selectedNode, node.rect?.width, node.rect?.height])

  React.useLayoutEffect(() => {
    const {flexLayout} = node

    if (flexLayout.justMaximizedOrRestored) {
      flexLayout.justMaximizedOrRestored = false
    } else {
      updateVisibleTabs()
    }
  })

  const getNear = (rect: Rect) => {
    if (!rect) return 0
    if (orientation === Orientation.HORZ) {
      return Math.max(rect.x, 0)
    } else {
      return Math.max(rect.y, 0)
    }
  }

  const getFar = (rect: Rect) => {
    if (!rect) return 0
    if (orientation === Orientation.HORZ) {
      return getNear(rect) + rect.width
    } else {
      return getNear(rect) + rect.height
    }
  }

  const getSize = (rect: DOMRect | Rect) => {
    if (!rect) return 0
    if (orientation === Orientation.HORZ) {
      return rect.width
    } else {
      return rect.height
    }
  }

  const updateVisibleTabs = () => {
    const tabMargin = 2
    if (firstRender.current === true) {
      tabsTruncated.current = false
    }
    const nodeRect = node instanceof TabSetNode ? node.rect : (node as BorderNode).tabHeaderRect!
    let lastChild = node.children[node.children.length - 1] as TabNode
    const stickyButtonsSize = stickyButtonsRef.current === null ? 0 : getSize(stickyButtonsRef.current!.getBoundingClientRect())

    if (!nodeRect) return

    if (
      firstRender.current === true ||
      nodeRect.width !== lastRect.current.width || // incase rect changed between first render and second
      nodeRect.height !== lastRect.current.height
    ) {
      lastRect.current = nodeRect
      const enabled = node instanceof TabSetNode ? node.enableTabStrip === true : true
      let endPos = getFar(nodeRect) - stickyButtonsSize
      if (toolbarRef.current !== null) {
        endPos -= getSize(toolbarRef.current.getBoundingClientRect())
      }
      if (enabled && node.children.length > 0) {
        if (hiddenTabs.length === 0 && position === 0 && getFar(lastChild.tabRect!) + tabMargin < endPos) {
          return // nothing to do all tabs are shown in available space
        }

        endPos -= hiddenTabs.length > 0 ? (orientation === Orientation.HORZ ? 10 : 0) : 45 // will need hidden tabs

        let shiftPos = 0

        const selectedTab = node.selectedNode as TabNode
        if (selectedTab && !userControlledLeft.current) {
          const selectedRect = selectedTab.tabRect!
          const selectedStart = getNear(selectedRect) - tabMargin
          const selectedEnd = getFar(selectedRect) + tabMargin

          // when selected tab is larger than available space then align left
          if (getSize(selectedRect) + 2 * tabMargin >= endPos - getNear(nodeRect)) {
            shiftPos = getNear(nodeRect) - selectedStart
          } else {
            if (selectedEnd > endPos || selectedStart < getNear(nodeRect)) {
              if (selectedStart < getNear(nodeRect)) {
                shiftPos = getNear(nodeRect) - selectedStart
              }
              // use second if statement to prevent tab moving back then forwards if not enough space for single tab
              if (selectedEnd + shiftPos > endPos) {
                shiftPos = endPos - selectedEnd
              }
            }
          }
        }

        const extraSpace = Math.max(0, endPos - (getFar(lastChild.tabRect!) + tabMargin + shiftPos))
        const newPosition = Math.min(0, position + shiftPos + extraSpace)

        // find hidden tabs
        const diff = newPosition - position
        const hidden: { node: TabNode; index: number }[] = []
        for (let i = 0; i < node.children.length; i++) {
          const child = node.children[i] as TabNode
          if (getNear(child.tabRect!) + diff < getNear(nodeRect!) || getFar(child.tabRect!) + diff > endPos) {
            hidden.push({node: child, index: i})
          }
        }

        if (hidden.length > 0) {
          tabsTruncated.current = true
        }

        firstRender.current = false // need to do a second render
        setHiddenTabs(hidden)
        !_.isNaN(newPosition) && setPosition(newPosition)
      }
    } else {
      firstRender.current = true
    }
  }

  const onMouseWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    let delta = 0
    if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
      delta = -event.deltaX
    } else {
      delta = -event.deltaY
    }
    if (event.deltaMode === 1) {
      // DOM_DELTA_LINE	0x01	The delta values are specified in lines.
      delta *= 40
    }
    !_.isNaN(position) && setPosition(position + delta)
    userControlledLeft.current = true
    event.stopPropagation()
  }

  return {selfRef, position, userControlledLeft, hiddenTabs, onMouseWheel, tabsTruncated: tabsTruncated.current}
}



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexlayout-react/src/view/TabOverflowHook.tsx